const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function clearClientAuthStorage() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('cachedUser');
    localStorage.removeItem('userCacheTime');
    localStorage.removeItem('impersonatedBy');
    localStorage.removeItem('originalAdminToken');
  } catch (_) {
    /* ignore */
  }
}

/** Fired so App can toast + sync React auth state (useAuth) and redirect to login. */
function dispatchSessionExpired(message) {
  clearClientAuthStorage();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('auth:session-expired', { detail: { message } })
    );
  }
}

function attachHttpStatus(err, status) {
  if (err && typeof err === 'object') err.status = status;
  return err;
}

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  /**
   * Raw fetch helpers: on 401, clear auth and notify UI (same as JSON api.request).
   */
  async _ensureAuthorizedOrThrow(response, requestUrl) {
    if (response.status !== 401) return;
    const isLogin = requestUrl.includes('/api/auth/login');
    if (isLogin) return;
    let message = 'Your session has expired. Please sign in again.';
    try {
      const errBody = await response.clone().json();
      if (errBody.detail !== undefined && errBody.detail !== null) {
        message =
          typeof errBody.detail === 'string'
            ? errBody.detail
            : String(errBody.detail?.message || errBody.detail?.msg || message);
      }
    } catch {
      /* ignore */
    }
    dispatchSessionExpired(message);
    throw attachHttpStatus(new Error(message), 401);
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getAuthHeaders(),
      ...options
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let message = 'Request failed';
        try {
          const errBody = await response.json();
          if (errBody.detail !== undefined && errBody.detail !== null) {
            if (Array.isArray(errBody.detail)) {
              message = errBody.detail
                .map((d) => (typeof d === 'object' && d.msg ? d.msg : String(d)))
                .join(' ');
            } else if (typeof errBody.detail === 'object') {
              message =
                errBody.detail.message ||
                errBody.detail.msg ||
                errBody.detail.description ||
                JSON.stringify(errBody.detail);
            } else {
              message = String(errBody.detail);
            }
          } else if (errBody.message) {
            message = String(errBody.message);
          }
        } catch {
          // non-JSON error body
        }

        const isLoginRequest = endpoint === '/api/auth/login' || url.endsWith('/api/auth/login');
        if (response.status === 401 && !isLoginRequest) {
          dispatchSessionExpired(message);
          throw attachHttpStatus(new Error(message), 401);
        }

        throw attachHttpStatus(new Error(message), response.status);
      }

      return await response.json();
    } catch (error) {
      if (error && error.status === 401) {
        throw error;
      }
      console.error('API Error:', error);
      throw error;
    }
  }

  // Authentication
  async login(credentials) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  async changePassword(passwordData) {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });
  }

  // Scrapers
  async getScrapers() {
    return this.request('/api/scrapers');
  }

  // Locations
  async getStates() {
    return this.request('/api/locations/states');
  }

  async getCounties(stateName) {
    return this.request(`/api/locations/counties/${stateName}`);
  }

  async getCities(stateName, countyName) {
    return this.request(`/api/locations/cities/${stateName}/${countyName}`);
  }

  // Craigslist
  async getCraigslistFilters() {
    return this.request('/api/craigslist/filters');
  }

  async getCraigslistStates() {
    return this.request('/api/craigslist/states');
  }

  async getCraigslistCities(stateId) {
    return this.request(`/api/craigslist/cities/${stateId}`);
  }

  async getZillowStates() {
    return this.request('/api/zillow/states');
  }

  async getRinkStates() {
    return this.request('/api/rink/states');
  }

  async getRinkCounties(stateId) {
    return this.request(`/api/rink/counties/${stateId}`);
  }

  // Contact Form
  async submitContactForm(contactData) {
    return this.request('/api/contact/submit', {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
  }

  // Client Reviews
  async getApprovedReviews() {
    return this.request('/api/reviews');
  }

  async submitReview(reviewData) {
    return this.request('/api/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData)
    });
  }

  async getAdminReviews(status = null, page = 1, pageSize = 50) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('page_size', pageSize);
    if (status) params.append('status', status);
    return this.request(`/api/reviews/admin?${params.toString()}`);
  }

  async getPendingReviewsCount() {
    return this.request('/api/reviews/admin/pending-count');
  }

  async approveReview(reviewId) {
    return this.request(`/api/reviews/admin/${reviewId}/approve`, {
      method: 'PUT'
    });
  }

  async rejectReview(reviewId) {
    return this.request(`/api/reviews/admin/${reviewId}/reject`, {
      method: 'PUT'
    });
  }

  async deleteReview(reviewId) {
    return this.request(`/api/reviews/admin/${reviewId}`, {
      method: 'DELETE'
    });
  }

  async getRinkCities(countyId) {
    return this.request(`/api/rink/cities/${countyId}`);
  }

  async createRinkState(data) {
    return this.request('/api/admin/rink/states', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteRinkState(stateId) {
    return this.request(`/api/admin/rink/states/${stateId}`, {
      method: 'DELETE'
    });
  }

  async createRinkCounty(data) {
    return this.request('/api/admin/rink/counties', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteRinkCounty(countyId) {
    return this.request(`/api/admin/rink/counties/${countyId}`, {
      method: 'DELETE'
    });
  }

  async createRinkCity(data) {
    return this.request('/api/admin/rink/cities', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteRinkCity(cityId) {
    return this.request(`/api/admin/rink/cities/${cityId}`, {
      method: 'DELETE'
    });
  }

  // Scraping Jobs
  async getJobs(page = 1, limit = 10, search = '') {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (search) params.append('search', search);
    
    return this.request(`/api/scraping/jobs?${params.toString()}`);
  }

  async getJobsStats() {
    return this.request('/api/scraping/jobs/stats');
  }

  async createRinkJob(jobData) {
    return this.request('/api/scraping/rink', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  }

  async createFacebookJob(jobData) {
    return this.request('/api/scraping/facebook', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  }

  async createGoogleJob(jobData) {
    return this.request('/api/scraping/google_my_business', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  }

  async createYellowPagesJob(jobData) {
    return this.request('/api/scraping/yellow_pages', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  }

  async createZillowJob(jobData) {
    return this.request('/api/scraping/zillow', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  }

  async createTruliaJob(jobData) {
    return this.request('/api/scraping/trulia', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  }

  async createCraigslistJob(jobData) {
    return this.request('/api/scraping/craigslist', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  }

  async getRinkResults(jobId) {
    return this.request(`/api/scraping/rink/results/${jobId}`);
  }

  async getUserRinkResults(limit = 10, offset = 0) {
    return this.request(`/api/scraping/rink/results?limit=${limit}&offset=${offset}`);
  }

  async getJobResults(jobId, page = 1, limit = 500) {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    const data = await this.request(`/api/scraping/jobs/${jobId}/results?${params.toString()}`);
    if (Array.isArray(data)) {
      return {
        results: data,
        pagination: {
          current_page: 1,
          total_pages: 1,
          total_count: data.length,
          limit: data.length,
          has_next: false,
          has_prev: false,
        },
      };
    }
    return data;
  }

  async deleteJob(jobId) {
    return this.request(`/api/scraping/jobs/${jobId}`, {
      method: 'DELETE'
    });
  }

  // Subscription
  async getUserSubscriptions() {
    return this.request('/api/subscriptions/user');
  }

  async cancelSubscription(subscriptionId) {
    return this.request(`/api/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST'
    });
  }

  async reactivateSubscription(subscriptionId) {
    return this.request(`/api/subscriptions/${subscriptionId}/reactivate`, {
      method: 'POST'
    });
  }

  async changeSubscriptionPlan(planData) {
    return this.request('/api/subscriptions/change-plan', {
      method: 'POST',
      body: JSON.stringify(planData)
    });
  }

  // Admin endpoints
  async getAllUsers(page = 1, pageSize = 20, search = null) {
    let url = `/api/admin/users?page=${page}&page_size=${pageSize}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return this.request(url);
  }

  async getUserDetails(userId) {
    return this.request(`/api/admin/users/${userId}/details`);
  }

  async sendClientEmail(userId, { subject, message }) {
    return this.request(`/api/admin/users/${userId}/email`, {
      method: 'POST',
      body: JSON.stringify({ subject, message })
    });
  }

  async getSentEmails(page = 1, pageSize = 20, search = '') {
    let url = `/api/admin/sent-emails?page=${page}&page_size=${pageSize}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return this.request(url);
  }

  async updateUser(userId, userData) {
    return this.request(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async toggleUserStatus(userId) {
    return this.request(`/api/admin/users/${userId}/toggle-status`, {
      method: 'PUT'
    });
  }

  async getSalesEmployees() {
    return this.request('/api/admin/sales-employees');
  }

  async getSalesEmployeePermissions(employeeId) {
    return this.request(`/api/admin/sales-employees/${employeeId}/permissions`);
  }

  async getSalesEmployeeCustomers(employeeId) {
    return this.request(`/api/admin/sales-employees/${employeeId}/customers`);
  }

  async getSalesEmployeeStats(employeeId) {
    return this.request(`/api/admin/sales-employees/${employeeId}/stats`);
  }

  async getUnassignedCustomers() {
    return this.request('/api/admin/customers/unassigned');
  }

  async assignCustomerToSalesEmployee(customerId, salesEmployeeId) {
    return this.request(`/api/admin/customers/${customerId}/assign-sales-employee`, {
      method: 'POST',
      body: JSON.stringify({ sales_employee_id: salesEmployeeId })
    });
  }

  async createSalesEmployee(employeeData) {
    return this.request('/api/auth/create-sales-employee', {
      method: 'POST',
      body: JSON.stringify(employeeData)
    });
  }

  async createUser(userData) {
    return this.request('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async toggleUserActiveStatus(userId) {
    return this.request(`/api/admin/users/${userId}/toggle-status`, {
      method: 'PUT'
    });
  }

  // Sales Employee Link Management
  async createSalesEmployeeLink(employeeId) {
    return this.request(`/api/admin/sales-employees/${employeeId}/create-link`, {
      method: 'POST'
    });
  }

  async getSalesEmployeeLinks(employeeId) {
    return this.request(`/api/admin/sales-employees/${employeeId}/links`);
  }

  async getAllSalesEmployeeLinks() {
    return this.request('/api/admin/sales-employee-links');
  }

  async updateSalesEmployeeLink(linkId, updateData) {
    return this.request(`/api/admin/sales-employee-links/${linkId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  async deleteSalesEmployeeLink(linkId) {
    return this.request(`/api/admin/sales-employee-links/${linkId}`, {
      method: 'DELETE'
    });
  }

  async registerWithReferral(userData) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async getUserActivity(userId) {
    return this.request(`/api/admin/users/${userId}/activity`);
  }

  async updateUserScrapeActivity(userId) {
    return this.request(`/api/admin/users/${userId}/update-scrape-activity`, {
      method: 'PUT'
    });
  }

  async getUserPermissions(userId) {
    return this.request(`/api/admin/users/${userId}/permissions`);
  }

  async updateUserPermissions(userId, scraperIds) {
    return this.request(`/api/admin/users/${userId}/permissions`, {
      method: 'POST',
      body: JSON.stringify(scraperIds)
    });
  }

  // Admin Reports
  async getSubscriptionSummaryReport() {
    return this.request('/api/admin/reports/subscription-summary');
  }

  async getDetailedExportReport() {
    return this.request('/api/admin/reports/detailed-export');
  }

  async getMonthlyRevenueReport(year) {
    return this.request(`/api/admin/reports/monthly-revenue?year=${year}`);
  }

  async getGrowthInsightsReport(year) {
    return this.request(`/api/admin/reports/growth-insights?year=${year}`);
  }

  async exportExcelReport(fromDate, toDate) {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    
    const token = localStorage.getItem('token');
    const url = `${this.baseURL}/api/admin/reports/excel-export?${params}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      await this._ensureAuthorizedOrThrow(response, url);
      throw new Error('Failed to export Excel report');
    }
    
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `admin_report_${fromDate || 'all'}_to_${toDate || 'now'}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
  }

  // Plan Management
  async getPlans() {
    return this.request('/api/subscriptions/admin/plans');
  }

  async getPlan(planId) {
    return this.request(`/api/subscriptions/admin/plans/${planId}`);
  }

  async createPlan(planData) {
    return this.request('/api/subscriptions/admin/plans', {
      method: 'POST',
      body: JSON.stringify(planData)
    });
  }

  async updatePlan(planId, planData) {
    return this.request(`/api/subscriptions/admin/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(planData)
    });
  }

  async deletePlan(planId) {
    return this.request(`/api/subscriptions/admin/plans/${planId}`, {
      method: 'DELETE'
    });
  }

  async getPublicPlans() {
    return this.request('/api/subscriptions/plans/public');
  }

  // Payment Methods
  async getPaymentMethod() {
    return this.request('/api/payment-methods');
  }

  async updatePaymentMethod(paymentData) {
    return this.request('/api/payment-methods', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async processPendingZillowProperties(limit = null) {
    return this.request('/api/scraping/zillow/process-pending', {
      method: 'POST',
      body: JSON.stringify({ limit })
    });
  }

  async getPendingZillowCount() {
    return this.request('/api/scraping/zillow/pending-count');
  }

  // Admin login as user
  async loginAsUser(userId) {
    return this.request(`/api/admin/users/${userId}/login-as`, {
      method: 'POST'
    });
  }

  // Zoom Call Records
  async getUserCallRecords(userId) {
    return this.request(`/api/zoom/users/${userId}/calls`);
  }

  async getAllCallRecords() {
    return this.request('/api/zoom/calls');
  }

  async deleteCallRecord(callId) {
    return this.request(`/api/zoom/calls/${callId}`, {
      method: 'DELETE'
    });
  }

  async downloadCallRecording(callRecordId) {
    // Download recording through backend proxy (handles Zoom authentication)
    const token = localStorage.getItem('token');
    const url = `${this.baseURL}/api/zoom/recordings/${callRecordId}/download`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      await this._ensureAuthorizedOrThrow(response, url);
      const error = await response.json().catch(() => ({ detail: 'Failed to download recording' }));
      throw attachHttpStatus(new Error(error.detail || 'Failed to download recording'), response.status);
    }
    
    // Get the blob and create a download link
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'call_recording.mp3';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    // Create and trigger download
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  }

  async exportCallRecordings(userId = null) {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);

    const exportUrl = `${this.baseURL}/api/zoom/calls/export${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(exportUrl, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      await this._ensureAuthorizedOrThrow(response, exportUrl);
      const error = await response.json().catch(() => ({ detail: 'Failed to export call recordings' }));
      throw attachHttpStatus(new Error(error.detail || 'Failed to export call recordings'), response.status);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'call_recordings.csv';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/i);
      if (match) {
        filename = match[1];
      }
    }

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  }

  async exportClientsWithCalls() {
    const token = localStorage.getItem('token');
    const clientsUrl = `${this.baseURL}/api/admin/reports/export-clients-with-calls`;
    const response = await fetch(clientsUrl, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      await this._ensureAuthorizedOrThrow(response, clientsUrl);
      const error = await response.json().catch(() => ({ detail: 'Failed to export clients with calls' }));
      throw attachHttpStatus(new Error(error.detail || 'Failed to export clients with calls'), response.status);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'clients_with_calls.csv';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/i);
      if (match) {
        filename = match[1];
      }
    }

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  }

  async exportClientsNeedingFollowup() {
    const token = localStorage.getItem('token');
    const followupUrl = `${this.baseURL}/api/admin/reports/export-clients-needing-followup`;
    const response = await fetch(followupUrl, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      await this._ensureAuthorizedOrThrow(response, followupUrl);
      const error = await response.json().catch(() => ({ detail: 'Failed to export clients needing follow-up' }));
      throw attachHttpStatus(new Error(error.detail || 'Failed to export clients needing follow-up'), response.status);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'clients_needing_followup.csv';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/i);
      if (match) {
        filename = match[1];
      }
    }

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  }

  async sendFollowupWhatsApp() {
    const token = localStorage.getItem('token');
    const waUrl = `${this.baseURL}/api/admin/reports/send-followup-whatsapp`;
    const response = await fetch(waUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      await this._ensureAuthorizedOrThrow(response, waUrl);
      const error = await response.json().catch(() => ({ detail: 'Failed to send WhatsApp follow-up summary' }));
      throw attachHttpStatus(new Error(error.detail || 'Failed to send WhatsApp follow-up summary'), response.status);
    }

    return response.json();
  }
}

export default new ApiService(); 